import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { get } from '../services/api';
import { hasFeature } from '../utils/featureFlags';

// ─── 作業種別カラー ────────────────────────────────────────────────────────────
const WORK_TYPE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  '航海':      { bg: '#3B82F6', border: '#1d4ed8', text: '#fff' },
  '荷役':      { bg: '#22C55E', border: '#15803d', text: '#fff' },
  '当直':      { bg: '#F59E0B', border: '#b45309', text: '#fff' },
  '点検・整備': { bg: '#6B7280', border: '#374151', text: '#fff' },
  '係船作業':  { bg: '#8B5CF6', border: '#6d28d9', text: '#fff' },
  '清掃':      { bg: '#14B8A6', border: '#0f766e', text: '#fff' },
  '訓練':      { bg: '#6366F1', border: '#4338ca', text: '#fff' },
  'その他':    { bg: '#94A3B8', border: '#64748b', text: '#fff' },
};
const DEFAULT_COLOR = { bg: '#60A5FA', border: '#2563eb', text: '#fff' };

function getColor(workType?: string | null) {
  if (!workType) return DEFAULT_COLOR;
  return WORK_TYPE_COLORS[workType] ?? DEFAULT_COLOR;
}

// ─── 型 ────────────────────────────────────────────────────────────────────────
export interface TimelineRecord {
  id: string; crewName: string; vessel: string; vessel_id?: string; date: string;
  clockInRaw: string; clockOutRaw: string;
  breakMin: number; workMin: number; overMin: number;
  workType?: string; workTypeStart?: string; status: string;
}
type AttendanceRecord = TimelineRecord;
interface User { id: string; name: string; vessel: string; }

interface ActivityLog {
  id: string; work_session_id: string;
  name: string; emoji: string; color: string;
  started_at: string; ended_at: string; duration_minutes: number;
}

interface BarSegment {
  rec: AttendanceRecord;
  left: number;
  width: number;
  isContinuation: boolean;
  continuesNext: boolean;
}

// ─── ユーティリティ ────────────────────────────────────────────────────────────
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function parseUtc(iso: string): Date {
  const s = iso.replace(' ', 'T');
  return new Date(s.includes('Z') || s.includes('+') ? s : s + 'Z');
}

function toJstHHMM(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('ja-JP', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false,
    });
  } catch { return ''; }
}

function fmtMinutes(min: number): string {
  if (!min) return '-';
  const h = Math.floor(min / 60), m = min % 60;
  return m > 0 ? `${h}h${m}m` : `${h}h`;
}

function buildSegments(
  rec: AttendanceRecord,
  viewYear: number,
  viewMonth: number,
): Array<{ day: number; seg: BarSegment }> {
  if (!rec.clockInRaw) return [];

  const startJst = parseUtc(rec.clockInRaw).getTime() + JST_OFFSET_MS;
  const endJst   = (rec.clockOutRaw
    ? parseUtc(rec.clockOutRaw).getTime()
    : Date.now()) + JST_OFFSET_MS;

  const startDayStart = Math.floor(startJst / 86400000) * 86400000;
  const endDayStart   = Math.floor(endJst   / 86400000) * 86400000;
  const numDays       = Math.round((endDayStart - startDayStart) / 86400000) + 1;

  if (numDays > 14) return [];

  const results: Array<{ day: number; seg: BarSegment }> = [];

  for (let i = 0; i < numDays; i++) {
    const dayMs  = startDayStart + i * 86400000;
    const dayObj = new Date(dayMs);

    const dy = dayObj.getUTCFullYear();
    const dm = dayObj.getUTCMonth() + 1;
    const dd = dayObj.getUTCDate();

    if (dy !== viewYear || dm !== viewMonth) continue;

    const segStartMs = Math.max(startJst, dayMs);
    const segEndMs   = Math.min(endJst,   dayMs + 86400000);

    const segStartMin = Math.floor((segStartMs - dayMs) / 60000);
    const segEndMin   = Math.min(1440, Math.floor((segEndMs - dayMs) / 60000));

    results.push({
      day: dd,
      seg: {
        rec,
        left:  (segStartMin / 1440) * 100,
        width: Math.max(0.4, ((segEndMin - segStartMin) / 1440) * 100),
        isContinuation: i > 0,
        continuesNext:  i < numDays - 1,
      },
    });
  }
  return results;
}

function activityBarPos(startedAt: string, endedAt: string, day: number, yr: number, mo: number) {
  const padM = String(mo).padStart(2, '0'), padD = String(day).padStart(2, '0');
  const dayStartMs = new Date(`${yr}-${padM}-${padD}T00:00:00+09:00`).getTime();
  const actStart   = new Date(startedAt).getTime();
  const actEnd     = new Date(endedAt).getTime();
  const c0 = Math.max(actStart, dayStartMs);
  const c1 = Math.min(actEnd,   dayStartMs + 86400000);
  if (c1 <= c0) return { left: 0, width: 0 };
  return { left: ((c0 - dayStartMs) / 86400000) * 100, width: Math.max(0.8, ((c1 - c0) / 86400000) * 100) };
}

// ─── ツールチップ ────────────────────────────────────────────────────────────
interface TooltipData {
  rec: AttendanceRecord;
  anchorX: number;
  anchorY: number;
  canEdit: boolean;
  isContinuation: boolean;
  continuesNext: boolean;
}

interface ActivityTooltipData {
  log: ActivityLog;
  anchorX: number;
  anchorY: number;
}

const ActivityTooltip: React.FC<{ data: ActivityTooltipData }> = ({ data: { log, anchorX, anchorY } }) => {
  const startStr = toJstHHMM(log.started_at);
  const endStr   = toJstHHMM(log.ended_at);
  return (
    <div style={{
      position: 'fixed', zIndex: 9999, pointerEvents: 'none',
      left: anchorX + 12, top: anchorY - 8,
      background: '#1e293b', color: '#f8fafc',
      borderRadius: 10, padding: '10px 14px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      fontSize: 12, lineHeight: 1.6, minWidth: 170,
      border: `1px solid ${log.color}66`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 16 }}>{log.emoji}</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#f1f5f9' }}>{log.name}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, color: '#cbd5e1', fontSize: 11 }}>
        <div>🕐 {startStr} 〜 {endStr}</div>
        <div>⏱ {fmtMinutes(log.duration_minutes)}</div>
      </div>
    </div>
  );
};

const Tooltip: React.FC<{ data: TooltipData }> = ({
  data: { rec, anchorX, anchorY, canEdit, isContinuation, continuesNext },
}) => {
  const color    = getColor(rec.workType);
  const isActive = rec.status === 'working';
  const hasMismatch = !!(rec.workTypeStart && rec.workType && rec.workTypeStart !== rec.workType);

  return (
    <div style={{
      position: 'fixed', zIndex: 9999, pointerEvents: 'none',
      left: anchorX + 12, top: anchorY - 8,
      background: '#1e293b', color: '#f8fafc',
      borderRadius: 10, padding: '10px 14px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      fontSize: 12, lineHeight: 1.6, minWidth: 190, maxWidth: 260,
      border: '1px solid #334155',
    }}>
      {(isContinuation || continuesNext) && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          {isContinuation && (
            <span style={{ fontSize: 10, background: '#7c3aed', color: '#fff', padding: '1px 7px', borderRadius: 99, fontWeight: 700 }}>← 前日から継続</span>
          )}
          {continuesNext && (
            <span style={{ fontSize: 10, background: '#0e7490', color: '#fff', padding: '1px 7px', borderRadius: 99, fontWeight: 700 }}>翌日に継続 →</span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: hasMismatch ? 4 : 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: 2, background: color.bg, flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: 13 }}>{rec.workType ?? '（種別未設定）'}</span>
        {isActive && !continuesNext && (
          <span style={{ fontSize: 10, background: '#22c55e', color: '#fff', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>作業中</span>
        )}
      </div>
      {hasMismatch && (
        <div style={{ fontSize: 10, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '3px 8px', marginBottom: 6, color: '#9a3412' }}>
          ⚠ 開始時: {rec.workTypeStart} → 終了時: {rec.workType}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', fontSize: 11 }}>
        <span style={{ color: '#94a3b8' }}>開始</span>
        <span style={{ color: '#f1f5f9', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {rec.date.slice(5)} {toJstHHMM(rec.clockInRaw)}
        </span>
        <span style={{ color: '#94a3b8' }}>終了</span>
        <span style={{ color: isActive ? '#4ade80' : '#f1f5f9', fontWeight: 600 }}>
          {isActive ? '作業中' : toJstHHMM(rec.clockOutRaw)}
        </span>
        <span style={{ color: '#94a3b8' }}>勤務</span>
        <span style={{ color: '#f1f5f9', fontWeight: 600 }}>{fmtMinutes(rec.workMin)}</span>
        {rec.overMin > 0 && (
          <>
            <span style={{ color: '#94a3b8' }}>残業</span>
            <span style={{ color: '#fca5a5', fontWeight: 700 }}>+{fmtMinutes(rec.overMin)}</span>
          </>
        )}
        <span style={{ color: '#94a3b8' }}>船名</span>
        <span style={{ color: '#f1f5f9' }}>{rec.vessel || '—'}</span>
      </div>
      {canEdit && (
        <div style={{ marginTop: 8, paddingTop: 7, borderTop: '1px solid #334155', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
          クリックして編集
        </div>
      )}
    </div>
  );
};

// ─── メインコンポーネント ───────────────────────────────────────────────────────
interface Props {
  users: User[];
  year: number;
  month: number;
  selectedUserId?: string;
  onUserChange?: (userId: string) => void;
  onEdit?: (rec: TimelineRecord) => void;
  onDelete?: (rec: TimelineRecord) => void;
  onActivity?: (rec: TimelineRecord) => void;
  onNewEntry?: (date: string, userId: string) => void;
  onActivityForDay?: (date: string, userId: string) => void;
  refreshKey?: number;
}

const AttendanceTimeline: React.FC<Props> = ({ users, year, month, selectedUserId: selectedUserIdProp, onUserChange, onEdit, onDelete, onActivity, onNewEntry, onActivityForDay, refreshKey = 0 }) => {
  const [selectedUserId, setSelectedUserId] = useState(() => selectedUserIdProp ?? users[0]?.id ?? '');
  const [records, setRecords]   = useState<AttendanceRecord[]>([]);
  const [loading, setLoading]   = useState(false);
  const [tooltip, setTooltip]         = useState<TooltipData | null>(null);
  const [actTooltip, setActTooltip]   = useState<ActivityTooltipData | null>(null);
  const [pickerDay, setPickerDay] = useState<number | null>(null);
  const [deletePickerDay, setDeletePickerDay] = useState<number | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const timelineRef = useRef<HTMLDivElement>(null);
  // 統合表示: 「特別作業」という区分を出さず、通常業務と同じ「業務」として見せる会社かどうか
  const unified = hasFeature('unified_work_types');

  useEffect(() => {
    if (!selectedUserId) return;
    setLoading(true);
    const padM    = String(month).padStart(2, '0');
    const lastDay = new Date(year, month, 0).getDate();
    Promise.all([
      get<{ data: any[] }>('/attendance', { user_id: selectedUserId, year: String(year), month: String(month) }),
      get<{ data: any[] }>('/activity-logs', {
        user_id:   selectedUserId,
        date_from: `${year}-${padM}-01`,
        date_to:   `${year}-${padM}-${String(lastDay).padStart(2, '0')}`,
      }),
    ]).then(([attR, actR]) => {
      // 管理者の操作（船員デモ・配乗の無い管理者の打刻）は勤怠ではないので、月の勤怠図と合計から外す
      setRecords((attR.data ?? []).filter((row: any) => Number(row.admin_operated ?? 0) !== 1).map((row: any) => ({
        id:            String(row.id ?? ''),
        crewName:      String(row.user_name ?? ''),
        vessel:        String(row.vessel_name ?? ''),
        date:          String(row.work_date ?? '').slice(0, 10),
        clockInRaw:    String(row.clock_in ?? ''),
        clockOutRaw:   String(row.clock_out ?? ''),
        breakMin:      Number(row.break_minutes ?? 60),
        workMin:       Number(row.work_minutes ?? 0),
        overMin:       Number(row.over_minutes ?? 0),
        vessel_id:     String(row.vessel_id ?? ''),
        workType:      row.work_type ?? undefined,
        workTypeStart: row.work_type_start ?? undefined,
        status:        String(row.status ?? 'completed'),
      })));
      setActivityLogs(((actR.data ?? []) as any[]).filter(l => Number(l.session_admin_operated ?? 0) !== 1) as ActivityLog[]);
    }).catch(() => { setRecords([]); setActivityLogs([]); }).finally(() => setLoading(false));
  }, [selectedUserId, year, month, refreshKey]);

  const daysInMonth = new Date(year, month, 0).getDate();

  // byDay: 開始日でグループ化（編集ボタン・日次集計用）
  const byDay: Record<number, AttendanceRecord[]> = {};
  for (let d = 1; d <= daysInMonth; d++) byDay[d] = [];
  for (const rec of records) {
    const day = parseInt(rec.date.slice(8, 10), 10);
    if (!isNaN(day) && byDay[day]) byDay[day]!.push(rec);
  }

  // barsByDay: 日またぎセグメント分割済みのバー（表示用）
  const barsByDay: Record<number, BarSegment[]> = {};
  for (let d = 1; d <= daysInMonth; d++) barsByDay[d] = [];
  for (const rec of records) {
    for (const { day, seg } of buildSegments(rec, year, month)) {
      barsByDay[day]?.push(seg);
    }
  }

  // actByDay: 特別作業ログを JST 日付でグループ化
  const actByDay: Record<number, ActivityLog[]> = {};
  for (let d = 1; d <= daysInMonth; d++) actByDay[d] = [];
  for (const log of activityLogs) {
    const jstDate = new Date(new Date(log.started_at).getTime() + JST_OFFSET_MS);
    if (jstDate.getUTCFullYear() === year && jstDate.getUTCMonth() + 1 === month) {
      actByDay[jstDate.getUTCDate()]?.push(log);
    }
  }

  // 日次集計（開始日基準）
  const daySummary: Record<number, { workMin: number; overMin: number }> = {};
  for (let d = 1; d <= daysInMonth; d++) {
    const recs = byDay[d] ?? [];
    daySummary[d] = {
      workMin: recs.reduce((s, r) => s + r.workMin, 0),
      overMin: recs.reduce((s, r) => s + r.overMin, 0),
    };
  }

  // 月次合計
  const totalWork = Object.values(daySummary).reduce((s, d) => s + d.workMin, 0);
  const totalOver = Object.values(daySummary).reduce((s, d) => s + d.overMin, 0);

  // 作業種別ごとの合計（開始日基準のセッションで集計）
  const workTypeTotals: Record<string, number> = {};
  const workTypeCounts: Record<string, number> = {};
  for (const rec of records) {
    const key = rec.workType ?? '（未設定）';
    workTypeTotals[key] = (workTypeTotals[key] ?? 0) + rec.workMin;
    workTypeCounts[key] = (workTypeCounts[key] ?? 0) + 1;
  }

  // 時間を指定して記録した作業の合計
  const totalActivityMin = activityLogs.reduce((s, l) => s + l.duration_minutes, 0);
  const activityTypeTotals: Record<string, { min: number; count: number; emoji: string; color: string }> = {};
  for (const log of activityLogs) {
    if (!activityTypeTotals[log.name]) activityTypeTotals[log.name] = { min: 0, count: 0, emoji: log.emoji, color: log.color };
    activityTypeTotals[log.name]!.min += log.duration_minutes;
    activityTypeTotals[log.name]!.count += 1;
  }

  /**
   * 統合表示の内訳: 「何をどれだけ作業したか」を作業名ごとの 合計時間 と 回数 でまとめる。
   * 回数は 出退勤の記録1件＝1回 ／ 作業ログ1件＝1回。同じ作業名なら合算する。
   */
  const unifiedTypeTotals = (() => {
    if (!unified) return [] as { name: string; min: number; count: number; emoji: string }[];
    const map: Record<string, { name: string; min: number; count: number; emoji: string }> = {};
    const put = (name: string, min: number, count: number, emoji: string) => {
      if (!map[name]) map[name] = { name, min: 0, count: 0, emoji };
      map[name]!.min   += min;
      map[name]!.count += count;
      if (!map[name]!.emoji) map[name]!.emoji = emoji;
    };
    for (const [name, min] of Object.entries(workTypeTotals)) put(name, min, workTypeCounts[name] ?? 0, '');
    for (const [name, v] of Object.entries(activityTypeTotals)) put(name, v.min, v.count, v.emoji);
    return Object.values(map).sort((a, b) => b.min - a.min);
  })();

  // 船別グループ（乗船中の船でまとめ、未配属は末尾）
  const vesselGroups = useMemo(() => {
    const map = new Map<string, User[]>();
    for (const u of users) {
      const key = u.vessel || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(u);
    }
    const groups = [...map.entries()].map(([vessel, members]) => ({ vessel, members }));
    groups.sort((a, b) => {
      if (!a.vessel && b.vessel) return 1;
      if (a.vessel && !b.vessel) return -1;
      return a.vessel.localeCompare(b.vessel, 'ja');
    });
    return groups;
  }, [users]);

  const selectedUser = users.find(u => u.id === selectedUserId);

  const handleBarEnter = useCallback((e: React.MouseEvent, seg: BarSegment) => {
    setTooltip({ rec: seg.rec, anchorX: e.clientX, anchorY: e.clientY, canEdit: !!onEdit, isContinuation: seg.isContinuation, continuesNext: seg.continuesNext });
  }, [onEdit]);
  const handleBarMove = useCallback((e: React.MouseEvent) => {
    setTooltip(prev => prev ? { ...prev, anchorX: e.clientX, anchorY: e.clientY } : null);
  }, []);
  const handleBarLeave  = useCallback(() => setTooltip(null), []);
  const handleBarClick  = useCallback((e: React.MouseEvent, rec: AttendanceRecord) => {
    e.stopPropagation(); setTooltip(null); setPickerDay(null); onEdit?.(rec);
  }, [onEdit]);
  const handleRowEditClick = useCallback((e: React.MouseEvent, day: number, dayRecs: AttendanceRecord[]) => {
    e.stopPropagation(); setTooltip(null); setDeletePickerDay(null);
    if (dayRecs.length === 1) { onEdit?.(dayRecs[0]!); setPickerDay(null); }
    else { setPickerDay(prev => prev === day ? null : day); }
  }, [onEdit]);

  const GUIDES = [0, 6, 12, 18, 24];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", userSelect: 'none' }}>
      {/* ヘッダーコントロール */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', fontWeight: 600 }}>
          船員：
          <select value={selectedUserId} onChange={e => { setSelectedUserId(e.target.value); onUserChange?.(e.target.value); setTooltip(null); setPickerDay(null); }}
            style={{ padding: '6px 10px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none', background: '#fff', minWidth: 200 }}>
            {vesselGroups.map(({ vessel, members }) =>
              vessel ? (
                <optgroup key={vessel} label={`🚢 ${vessel}（${members.length}名）`}>
                  {members.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </optgroup>
              ) : (
                <optgroup key="__none__" label="（未配属）">
                  {members.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </optgroup>
              )
            )}
          </select>
        </label>
        <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
          {year}年{month}月 / {records.length}セッション
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>読み込み中...</div>
      ) : (
        <div ref={timelineRef}>
          {/* ヘッダー行 */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
            <div style={{ width: 48, flexShrink: 0 }} />
            <div style={{ flex: 1, position: 'relative', height: 22 }}>
              {GUIDES.map(h => (
                <div key={h} style={{
                  position: 'absolute', left: `${(h / 24) * 100}%`,
                  fontSize: 12, color: '#6b7280', fontWeight: 600,
                  transform: h === 24 ? 'translateX(-100%)' : h > 0 ? 'translateX(-50%)' : 'none',
                  top: 4, whiteSpace: 'nowrap',
                }}>
                  {h}
                </div>
              ))}
            </div>
            <div style={{ width: 62, textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#374151', paddingRight: 4 }}>勤務</div>
            <div style={{ width: 56, textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#dc2626', paddingRight: 4 }}>残業</div>
            <div style={{ width: 52, textAlign: 'right', fontSize: 12, fontWeight: 700, color: unified ? '#1d4ed8' : '#7c3aed', paddingRight: 4 }}>{unified ? '作業' : '特別'}</div>
            {(onEdit || onDelete || onActivity || onNewEntry || onActivityForDay) && <div style={{ width: 80, flexShrink: 0 }} />}
          </div>

          {/* 日付行 */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day  = i + 1;
            const dow  = new Date(year, month - 1, day).getDay();
            const isSun = dow === 0, isSat = dow === 6;
            const today = new Date();
            const isToday = year === today.getFullYear() && month === today.getMonth() + 1 && day === today.getDate();
            const dateColor = isSun ? '#dc2626' : isSat ? '#2563eb' : '#374151';
            const rowBg     = isToday ? '#fffbeb' : isSun ? '#fff5f5' : isSat ? '#eff6ff' : '#fff';
            const summary   = daySummary[day]!;
            const dayRecs   = byDay[day] ?? [];
            const segs      = barsByDay[day] ?? [];
            const showPicker = pickerDay === day && dayRecs.length > 1;

            // 不一致警告がある日かどうか（その日開始のセッション）
            const hasMismatchDay = dayRecs.some(r => r.workTypeStart && r.workType && r.workTypeStart !== r.workType);
            const dayActivities  = actByDay[day] ?? [];
            const dayActMin      = dayActivities.reduce((s, l) => s + l.duration_minutes, 0);

            return (
              <React.Fragment key={day}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  background: rowBg,
                  borderTop: '1px solid #e2e8f0',
                  borderLeft: isToday ? '3px solid #f59e0b' : '3px solid transparent',
                }}>
                  {/* 日付 */}
                  <div style={{ width: 48, flexShrink: 0, padding: '6px 8px', textAlign: 'right', fontWeight: isToday ? 800 : 600, color: isToday ? '#f59e0b' : dateColor, fontSize: 13 }}>
                    {day}
                  </div>

                  {/* タイムライン */}
                  <div style={{ flex: 1, position: 'relative', background: '#f8fafc', borderRadius: 4, margin: '3px 6px' }}>
                    {/* ガイドライン（全高さスパン・クリック非干渉） */}
                    {GUIDES.slice(1, -1).map(h => (
                      <div key={h} style={{ position: 'absolute', left: `${(h / 24) * 100}%`, top: 0, bottom: 0, width: 1, background: '#e2e8f0', zIndex: 1, pointerEvents: 'none' }} />
                    ))}
                    {/* 枠線（クリック非干渉） */}
                    <div style={{ position: 'absolute', inset: 0, border: '1px solid #e5e7eb', borderRadius: 4, pointerEvents: 'none', zIndex: 1 }} />

                    {/* セッションバー track */}
                    <div style={{ position: 'relative', height: 36, zIndex: 2 }}>
                    {segs.map((seg, si) => {
                      const { rec, left, width, isContinuation, continuesNext } = seg;
                      const color    = getColor(rec.workType);
                      const isActive = rec.status === 'working' && !continuesNext;
                      const hasMismatch = !!(rec.workTypeStart && rec.workType && rec.workTypeStart !== rec.workType);
                      const borderRadius = `${isContinuation ? 0 : 4}px ${continuesNext ? 0 : 4}px ${continuesNext ? 0 : 4}px ${isContinuation ? 0 : 4}px`;
                      const borderLeft   = isContinuation  ? 'none' : `1px solid ${color.border}`;
                      const borderRight  = continuesNext   ? 'none' : `1px solid ${color.border}`;
                      const borderTopBottom = hasMismatch ? '2px solid #f97316' : rec.overMin > 0 ? '2px solid #dc2626' : `1px solid ${color.border}`;

                      return (
                        <div key={`${rec.id}-${si}`}
                          title={onEdit ? 'クリックして編集' : undefined}
                          style={{
                            position: 'absolute', zIndex: 2,
                            left: `${left}%`, width: `${width}%`,
                            top: 4, bottom: 4,
                            background: isActive
                              ? `repeating-linear-gradient(45deg, ${color.bg}, ${color.bg} 4px, ${color.border} 4px, ${color.border} 8px)`
                              : color.bg,
                            borderRadius,
                            borderLeft, borderRight,
                            borderTop:    borderTopBottom,
                            borderBottom: borderTopBottom,
                            cursor: onEdit ? 'pointer' : 'default',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden', minWidth: 4,
                          }}
                          onMouseEnter={e => handleBarEnter(e, seg)}
                          onMouseMove={handleBarMove}
                          onMouseLeave={handleBarLeave}
                          onClick={onEdit ? e => handleBarClick(e, rec) : undefined}
                        >
                          {width > 5 && (
                            <span style={{ fontSize: 11, fontWeight: 800, color: color.text, textShadow: '0 1px 2px rgba(0,0,0,0.3)', pointerEvents: 'none', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                              {isContinuation && '↩ '}{rec.workType ?? ''}{hasMismatch && ' ⚠'}{continuesNext && ' →'}
                            </span>
                          )}
                          {/* 不一致警告 ドット */}
                          {hasMismatch && !isContinuation && (
                            <span style={{ position: 'absolute', top: -4, right: -4, background: '#f97316', color: '#fff', borderRadius: '50%', width: 12, height: 12, fontSize: 8, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, pointerEvents: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>!</span>
                          )}
                        </div>
                      );
                    })}
                    </div>

                    {/* 特別作業バー track */}
                    {dayActivities.length > 0 && (
                      <div style={{ position: 'relative', height: 20, borderTop: '1px dashed #ddd6fe', zIndex: 2 }}>
                        {dayActivities.map(log => {
                          const pos = activityBarPos(log.started_at, log.ended_at, day, year, month);
                          if (pos.width === 0) return null;
                          return (
                            <div key={log.id}
                              onMouseEnter={e => setActTooltip({ log, anchorX: e.clientX, anchorY: e.clientY })}
                              onMouseMove={e => setActTooltip(t => t ? { ...t, anchorX: e.clientX, anchorY: e.clientY } : null)}
                              onMouseLeave={() => setActTooltip(null)}
                              style={{
                                position: 'absolute',
                                left: `${pos.left}%`, width: `${pos.width}%`,
                                top: 3, bottom: 3,
                                background: log.color,
                                borderRadius: 3,
                                border: `1px solid ${log.color}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                overflow: 'hidden', minWidth: 4, cursor: 'default',
                              }}
                            >
                              {pos.width > 3 && (
                                <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.4)', pointerEvents: 'none', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                  {log.emoji}{pos.width > 6 ? ' ' + log.name : ''}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* 勤務時間 */}
                  <div style={{ width: 62, textAlign: 'right', fontSize: 13, fontWeight: 700, color: summary.workMin > 0 ? '#374151' : '#d1d5db', paddingRight: 4, whiteSpace: 'nowrap' }}>
                    {summary.workMin > 0 ? fmtMinutes(summary.workMin) : ''}
                  </div>
                  {/* 残業 */}
                  <div style={{ width: 56, textAlign: 'right', fontSize: 13, fontWeight: 700, paddingRight: 4, whiteSpace: 'nowrap' }}>
                    {summary.overMin > 0 ? <span style={{ color: '#dc2626' }}>+{fmtMinutes(summary.overMin)}</span> : ''}
                  </div>
                  {/* 特別作業 */}
                  <div style={{ width: 52, textAlign: 'right', fontSize: 12, fontWeight: 700, paddingRight: 4, whiteSpace: 'nowrap' }}>
                    {dayActMin > 0 ? <span style={{ color: unified ? '#1d4ed8' : '#7c3aed' }}>{fmtMinutes(dayActMin)}</span> : ''}
                  </div>

                  {/* 編集・削除・特別作業・追加ボタン */}
                  {(onEdit || onDelete || onActivity || onNewEntry || onActivityForDay) && (
                    <div style={{ width: 80, flexShrink: 0, display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                      {/* 空の日：勤怠追加 + 特別作業ボタン */}
                      {(dayRecs.length === 0 && !segs.some(s => s.isContinuation)) && (onNewEntry || onActivityForDay) && (
                        <>
                          {onNewEntry && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                                onNewEntry(dateStr, selectedUserId);
                              }}
                              title="この日の勤怠を追加"
                              style={{
                                width: 32, height: 32, border: '1.5px dashed #94a3b8', borderRadius: 6,
                                background: '#f8fafc', color: '#64748b',
                                cursor: 'pointer', fontSize: 16, fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              +
                            </button>
                          )}
                          {onActivityForDay && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                                onActivityForDay(dateStr, selectedUserId);
                              }}
                              title={unified ? '作業を追加' : '特別作業を追加'}
                              style={{
                                width: 32, height: 32, borderRadius: 6,
                                border: `1.5px dashed ${unified ? '#bfdbfe' : '#c4b5fd'}`,
                                background: unified ? '#eff6ff' : '#faf5ff', color: unified ? '#1d4ed8' : '#7c3aed',
                                cursor: 'pointer', fontSize: 11, fontWeight: 800,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              {unified ? '作' : '特'}
                            </button>
                          )}
                        </>
                      )}
                      {(dayRecs.length > 0 || segs.some(s => s.isContinuation)) && (
                        <>
                          {onEdit && (
                            <button
                              onClick={e => {
                                const editRecs = dayRecs.length > 0 ? dayRecs : segs.map(s => s.rec).filter((r, ri, arr) => arr.findIndex(x => x.id === r.id) === ri);
                                handleRowEditClick(e, day, editRecs);
                              }}
                              title="この日の勤怠を編集"
                              style={{
                                width: 28, height: 28, border: 'none', borderRadius: 6,
                                background: showPicker ? '#1d4ed8' : hasMismatchDay ? '#fff7ed' : '#eff6ff',
                                color: showPicker ? '#fff' : hasMismatchDay ? '#ea580c' : '#1d4ed8',
                                cursor: 'pointer', fontSize: hasMismatchDay ? 15 : 14,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              {hasMismatchDay ? '⚠' : '✎'}
                            </button>
                          )}
                          {onDelete && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setPickerDay(null);
                                const delRecs = dayRecs.length > 0 ? dayRecs : segs.map(s => s.rec).filter((r, ri, arr) => arr.findIndex(x => x.id === r.id) === ri);
                                if (delRecs.length === 1) { onDelete(delRecs[0]!); }
                                else { setDeletePickerDay(prev => prev === day ? null : day); }
                              }}
                              title="この日の勤怠を削除"
                              style={{
                                width: 28, height: 28, border: 'none', borderRadius: 6,
                                background: '#fef2f2', color: '#dc2626',
                                cursor: 'pointer', fontSize: 14,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              🗑
                            </button>
                          )}
                          {onActivity && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                const actRecs = dayRecs.length > 0 ? dayRecs : segs.map(s => s.rec).filter((r, ri, arr) => arr.findIndex(x => x.id === r.id) === ri);
                                if (actRecs[0]) onActivity(actRecs[0]);
                              }}
                              title={unified ? '作業を追加' : '特別作業を追加'}
                              style={{
                                width: 28, height: 28, border: 'none', borderRadius: 6,
                                background: unified ? '#eff6ff' : '#f5f3ff', color: unified ? '#1d4ed8' : '#7c3aed',
                                cursor: 'pointer', fontSize: 11, fontWeight: 800,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              {unified ? '作' : '特'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* 複数セッション選択ピッカー（編集） */}
                {showPicker && (() => {
                  const editRecs = dayRecs.length > 0 ? dayRecs : segs.map(s => s.rec).filter((r, ri, arr) => arr.findIndex(x => x.id === r.id) === ri);
                  return (
                    <div style={{ background: '#f0f9ff', borderLeft: '3px solid #1d4ed8', padding: '6px 12px 6px 44px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, alignSelf: 'center', marginRight: 4 }}>セッションを選択：</span>
                      {editRecs.map((rec, idx) => {
                        const color = getColor(rec.workType);
                        const mismatch = rec.workTypeStart && rec.workType && rec.workTypeStart !== rec.workType;
                        return (
                          <button key={rec.id} onClick={() => { setPickerDay(null); onEdit?.(rec); }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: `1px solid ${color.border}`, borderRadius: 20, background: color.bg, color: color.text, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                            {idx + 1}. {rec.date.slice(5)} {toJstHHMM(rec.clockInRaw)}〜{rec.clockOutRaw ? toJstHHMM(rec.clockOutRaw) : '作業中'}
                            {rec.workType && <span style={{ opacity: 0.85 }}>（{rec.workType}）</span>}
                            {mismatch && <span style={{ background: '#f97316', color: '#fff', borderRadius: 99, padding: '0 4px', fontSize: 9, fontWeight: 900 }}>!</span>}
                          </button>
                        );
                      })}
                      <button onClick={() => setPickerDay(null)} style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 20, background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 11 }}>✕</button>
                    </div>
                  );
                })()}
                {/* 複数セッション選択ピッカー（削除） */}
                {deletePickerDay === day && (() => {
                  const delRecs = dayRecs.length > 0 ? dayRecs : segs.map(s => s.rec).filter((r, ri, arr) => arr.findIndex(x => x.id === r.id) === ri);
                  return (
                    <div style={{ background: '#fef2f2', borderLeft: '3px solid #dc2626', padding: '6px 12px 6px 44px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, alignSelf: 'center', marginRight: 4 }}>削除するセッションを選択：</span>
                      {delRecs.map((rec, idx) => (
                        <button key={rec.id} onClick={() => { setDeletePickerDay(null); onDelete?.(rec); }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: '1px solid #fca5a5', borderRadius: 20, background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                          {idx + 1}. {rec.date.slice(5)} {toJstHHMM(rec.clockInRaw)}〜{rec.clockOutRaw ? toJstHHMM(rec.clockOutRaw) : '作業中'}
                          {rec.workType && <span style={{ opacity: 0.7 }}>（{rec.workType}）</span>}
                        </button>
                      ))}
                      <button onClick={() => setDeletePickerDay(null)} style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 20, background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 11 }}>✕</button>
                    </div>
                  );
                })()}
              </React.Fragment>
            );
          })}

          {/* 月次合計行 */}
          <div style={{ display: 'flex', alignItems: 'center', background: '#eff6ff', borderTop: '2px solid #93c5fd', marginTop: 2, borderRadius: '0 0 8px 8px' }}>
            <div style={{ width: 48, flexShrink: 0, padding: '8px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>計</div>
            <div style={{ flex: 1 }} />
            <div style={{ width: 62, textAlign: 'right', fontSize: 14, fontWeight: 800, color: '#1d4ed8', paddingRight: 4 }}>{fmtMinutes(totalWork)}</div>
            <div style={{ width: 56, textAlign: 'right', fontSize: 14, fontWeight: 800, color: '#dc2626', paddingRight: 4 }}>
              {totalOver > 0 ? `+${fmtMinutes(totalOver)}` : '-'}
            </div>
            <div style={{ width: 52, textAlign: 'right', fontSize: 14, fontWeight: 800, color: unified ? '#1d4ed8' : '#7c3aed', paddingRight: 4 }}>
              {totalActivityMin > 0 ? fmtMinutes(totalActivityMin) : '-'}
            </div>
            {(onEdit || onDelete || onActivity || onNewEntry || onActivityForDay) && <div style={{ width: 80, flexShrink: 0 }} />}
          </div>

          {/* 統合表示: 作業名ごとの「合計時間」と「回数」を1か所にまとめて出す（区分は出さない） */}
          {unified ? (
            unifiedTypeTotals.length > 0 && (
              <div style={{ marginTop: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8 }}>業務内容ごとの時間と回数</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {unifiedTypeTotals.map(t => {
                    const color = getColor(t.name === '（未設定）' ? undefined : t.name);
                    return (
                      <div key={t.name} style={{ display: 'flex', alignItems: 'center', gap: 6, background: color.bg, color: color.text, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
                        {t.emoji && <span>{t.emoji}</span>}
                        <span>{t.name}</span>
                        <span style={{ opacity: 0.85, fontSize: 11 }}>{fmtMinutes(t.min)}</span>
                        <span style={{ opacity: 0.75, fontSize: 11 }}>（{t.count}回）</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ) : (<>
          {/* 作業種別ごとの合計 */}
          {Object.keys(workTypeTotals).length > 0 && (
            <div style={{ marginTop: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8 }}>作業種別 合計</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(workTypeTotals).sort((a, b) => b[1] - a[1]).map(([type, min]) => {
                  const color = getColor(type === '（未設定）' ? undefined : type);
                  return (
                    <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, background: color.bg, color: color.text, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
                      <span>{type}</span>
                      <span style={{ opacity: 0.85, fontSize: 11 }}>{fmtMinutes(min)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 特別作業ごとの合計 */}
          {Object.keys(activityTypeTotals).length > 0 && (
            <div style={{ marginTop: 12, background: '#faf5ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>特別作業 合計</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(activityTypeTotals).sort((a, b) => b[1].min - a[1].min).map(([name, { min, emoji, color }]) => (
                  <div key={name} style={{
                    display: 'flex', alignItems: 'center', gap: 5, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700,
                    background: color + '22', color, border: `1px solid ${color}44`,
                  }}>
                    <span>{emoji}</span>
                    <span>{name}</span>
                    <span style={{ opacity: 0.8, fontSize: 11 }}>{fmtMinutes(min)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>)}
        </div>
      )}

      {/* 凡例 */}
      <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>作業種別：</span>
        {[...Object.entries(WORK_TYPE_COLORS), ['（未設定）', DEFAULT_COLOR] as any].map(([type, color]: any) => (
          <span key={type} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#374151' }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, background: color.bg, display: 'inline-block', flexShrink: 0 }} />
            {type}
          </span>
        ))}
        <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#dc2626' }}>
          <span style={{ width: 12, height: 12, borderRadius: 2, border: '2px solid #dc2626', display: 'inline-block' }} />
          残業あり
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#f97316' }}>
          <span style={{ background: '#f97316', color: '#fff', borderRadius: '50%', width: 12, height: 12, fontSize: 8, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>!</span>
          開始/終了 作業種別不一致
        </span>
      </div>

      {tooltip && <Tooltip data={tooltip} />}
      {actTooltip && <ActivityTooltip data={actTooltip} />}
    </div>
  );
};

export default AttendanceTimeline;
